import showMeCarfaxLogo from '../assets/show-me-carfax.png';

/** Final CARFAX badge/logo for shopper-facing cards and detail pages. */
export const DEFAULT_CARFAX_BADGE_IMAGE_URL = showMeCarfaxLogo;

/** True if URL is CARFAX’s consumer “buy a report” page (not dealer inventory report). */
export function isCarfaxConsumerPaywallUrl(s) {
  if (!s || typeof s !== 'string') return false;
  const lower = s.toLowerCase();
  return lower.includes('creditcard.cfx') || lower.includes('/creditcard');
}

/** Dealer portal VHR path — not for public shoppers; server should serve Report.cfx instead. */
export function isCarfaxDealerPortalVhrUrl(s) {
  if (!s || typeof s !== 'string') return false;
  return /carfaxonline\.com\/vhr\//i.test(s);
}

/**
 * First usable CARFAX link for shoppers: prefer server-resolved href, then stored URL if not paywall.
 * Ignores creditCard.cfx and carfaxonline.com/vhr/{VIN} dealer links.
 */
export function pickPublicCarfaxHref(car) {
  if (!car || typeof car !== 'object') return null;
  const candidates = [
    car.carfaxReportHref,
    car.carfax_report_href,
    car.carfaxReportUrl,
    car.carfax_report_url,
  ];
  for (const c of candidates) {
    if (typeof c !== 'string' || !c.trim()) continue;
    const t = c.trim();
    if (!/^https?:\/\//i.test(t)) continue;
    if (isCarfaxConsumerPaywallUrl(t)) continue;
    if (isCarfaxDealerPortalVhrUrl(t)) continue;
    return t;
  }
  return null;
}

/** 17-char VIN without I, O, Q (NHTSA-style). */
export function normalizeVinForCarfax(vin) {
  if (vin == null || typeof vin !== 'string') return null;
  const v = vin.trim().toUpperCase().replace(/\s/g, '');
  if (v.length !== 17) return null;
  if (!/^[A-HJ-NPR-Z0-9]{17}$/.test(v)) return null;
  return v;
}

/** Partner id from CARFAX (e.g. DVW_1). Use REACT_APP_CARFAX_PARTNER_CODE in the admin client .env for form preview. */
export function sanitizeCarfaxPartnerCode(raw) {
  if (raw == null) return null;
  const s = String(raw).trim();
  if (!s) return null;
  if (s.length > 80) return null;
  if (!/^[A-Za-z0-9_-]+$/.test(s)) return null;
  return s;
}

/**
 * Canonical public inventory link: …/Report.cfx?partner=…&vin=…
 * @param {unknown} vin
 * @param {unknown} partnerCode
 * @returns {string|null}
 */
export function buildCarfaxReportCfxUrl(vin, partnerCode) {
  const v = normalizeVinForCarfax(vin);
  const p = sanitizeCarfaxPartnerCode(partnerCode);
  if (!v || !p) return null;
  const u = new URL('https://www.carfax.com/VehicleHistory/p/Report.cfx');
  u.searchParams.set('partner', p);
  u.searchParams.set('vin', v);
  return u.toString();
}

/**
 * True if a stored CARFAX URL refers to the same VIN (query ?vin= or dealer …/vhr/{VIN}).
 * Used so the admin form can show a server-saved URL when REACT_APP_CARFAX_PARTNER_CODE is unset.
 */
export function carfaxStoredUrlMatchesVin(url, vin) {
  const v = normalizeVinForCarfax(vin);
  if (!v || !url || typeof url !== 'string') return false;
  const s = url.trim();
  if (!s || isCarfaxConsumerPaywallUrl(s)) return false;
  try {
    const u = new URL(s);
    if (!u.hostname.toLowerCase().includes('carfax')) return false;
    const q = u.searchParams.get('vin') || u.searchParams.get('VIN');
    if (normalizeVinForCarfax(q) === v) return true;
    const m = u.pathname.match(/\/vhr\/([A-HJ-NPR-Z0-9]{17})(?:\/|$)/i);
    if (m && normalizeVinForCarfax(m[1]) === v) return true;
  } catch {
    return false;
  }
  return false;
}
